import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextField from "@mui/material/TextField";
import * as Yup from "yup";
import { debounce } from "lodash";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { customerSelector } from "../../store/slices/customerSlice";
import regex from "../../utils/regex";
import { ERRORS_MESSAGE, fieldRequiredMessage } from "../../constants";
import { addEmail } from "../../store/slices/applicationFormSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";

export default function Email({ email, customerId, fieldErrors, setFieldErrors, applicationId, disabled }) {
  const dispatch = useDispatch();
  const { isCustomerLogin } = useSelector(customerSelector);

  const [allValues, setAllValue] = useState({
    address: email?.address,
  });

  useEffect(() => {
    if (email?.address) {
      setAllValue({ address: email?.address });
    } else {
      setAllValue({ address: "" });
    }
  }, [email]);

  const validationSchema = Yup.object({
    address: Yup.string()
      .required(fieldRequiredMessage.address)
      .matches(regex.email, "Please enter valid email address")
      .min(2, "Minimum of 5 characters")
      .max(50, "Maximum of 50 characters"),
  });

  const debouncedValidation = useCallback(
    debounce(async (name, fieldName, value) => {
      try {
        await validationSchema.validateAt(name, { [name]: value });
        setFieldErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
      } catch (error) {
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: error.message,
        }));
      }
    }, 300),
    [],
  );

  const onChangeField = (e, nameField) => {
    let isValid = true;
    const name = nameField.split("_")[0];
    isValid = e?.length <= 50;
    if (isValid) {
      setAllValue({ ...allValues, [name]: e });
    }
    debouncedValidation(name, nameField, e);
  };

  const blurFn = (fieldName, value) => {
    try {
      let fieldErr = fieldErrors[fieldName] || "";
      const isValid = regex.email.test(value);

      if (isValid) {
        fieldErr = "";
      } else {
        fieldErr = "Please enter valid email address";
      }

      if (fieldErr === "") {
        if (email?.address === value || value === "" || isCustomerLogin) {
          return;
        }

        dispatch(
          addEmail({
            data: {
              emailId: email?._id,
              customerId,
              address: value,
              applicationId,
            },
          }),
        ).then((res) => {
          if (res?.error) {
            dispatch(
              openSnackbar({
                message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
                intent: "danger",
              }),
            );
          }
        });
      }
    } catch (error) {
      const newErrors = {};
      error.inner.forEach((validationError) => {
        newErrors[validationError.path] = validationError.message;
      });
      setFieldErrors(newErrors);
    }
  };

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName }) =>
    getCustomerOrEntityLocalAndLenderError({ fieldError, localState: allValues, fieldName, localFieldName });

  const emailValidationError =
    getLocalAndLenderError({
      fieldError: fieldErrors[`address_${customerId}`],
      fieldName: `emails?.slice(-1)[0].address_${customerId}`,
      localFieldName: "address",
    }) || "";

  return (
    <React.Fragment>
      <TextField
        id={`email_${customerId}`}
        type="text"
        label="Email"
        variant="filled"
        autoComplete="off"
        size="small"
        fullWidth
        name={`address_${customerId}`}
        disabled={disabled || false}
        value={allValues?.address || ""}
        error={emailValidationError}
        helperText={emailValidationError}
        onChange={(event) => onChangeField(event?.target?.value, `address_${customerId}`)}
        onBlur={(e) => blurFn(`address_${customerId}`, e?.target?.value)}
      />
    </React.Fragment>
  );
}
